import { Location } from "@angular/common";
import {
    AfterContentInit,
    Component,
    ContentChildren,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChild
} from "@angular/core";
import { UserService } from "@main/users";
import { PrimeTemplate } from "primeng/api";
import { FormStateComponent } from "@mipro/components/form-state.component";

@Component({
    selector: "form-splitter",
    template: `
        <div class="m-formsplitter">
            <ng-container *ngTemplateOutlet="headerTmpl"></ng-container>
            <ng-container *ngIf="!headerTmpl">
                <div class="m-formsplitter-header">
                    <m-icon-svg *ngIf="headerIcon" [iconName]="headerIcon"></m-icon-svg>
                    <h2 class="m-formsplitter-header-text">{{ header }} <form-state #formState></form-state></h2>
                </div>
                <p-message
                    *ngIf="subHeaderWarningMessage"
                    [showTransitionOptions]="'0ms'"
                    [hideTransitionOptions]="'0ms'"
                    severity="warn"
                    [text]="subHeaderWarningMessage">
                </p-message>
                <p-button
                    *ngIf="subHeaderButtonMessage"
                    class="ribbon-header-options-button"
                    [label]="subHeaderButtonMessage"
                    icon="pi pi-save"
                    styleClass="m-button-primary"
                    (onClick)="subHeaderButton.emit()"></p-button>
            </ng-container>

            <p-splitter [panelSizes]="[25, 75]" [minSizes]="[20]" stateStorage="local" [stateKey]="stateKey" [layout]="layout">
                <ng-template pTemplate>
                    <div class="m-formsplitter-nlhs">
                        <ng-container *ngTemplateOutlet="leftPanelHeaderTmpl"></ng-container>
                        <div *ngIf="!leftPanelHeaderTmpl" class="m-formsplitter-nlhs-header">
                            {{ leftSubHeader }}
                        </div>
                        <form class="m-formsplitter-nlhs-content-top" (ngSubmit)="ngLeftSubmit.emit()">
                            <ng-container *ngTemplateOutlet="leftPanelTmpl"></ng-container>
                        </form>
                        <div *ngIf="ravenEnabled && ravenValidConfig" class="m-formsplitter-erhs-header">
                            {{ eRightSubHeader }}
                        </div>
                        <form class="m-formsplitter-nlhs-content-bottom" (ngSubmit)="ngERightSubmit.emit()">
                            <ng-container *ngTemplateOutlet="eRightPanelTmpl"></ng-container>
                        </form>
                    </div>
                    <div class="m-formsplitter-lhs-divider"></div>
                </ng-template>
                <ng-template pTemplate>
                    <div class="m-formsplitter-rhs">
                        <form (ngSubmit)="ngRightSubmit.emit()">
                            <div class="m-formsplitter-rhs-header">
                                <h3 class="m-formsplitter-rhs-header-title">{{ rightSubHeader }}</h3>
                                <div class="flex align-items-center">
                                    <ng-container *ngTemplateOutlet="rightPanelHeaderActionsTmpl"></ng-container>
                                </div>
                            </div>
                            <div class="m-formsplitter-rhs-content">
                                <div class="m-formsplitter-rhs-content-top">
                                    <ng-container *ngTemplateOutlet="rightPanelTopTmpl"></ng-container>
                                </div>
                                <div *ngIf="rightPanelBottomTmpl" class="m-formsplitter-rhs-content-bottom">
                                    <ng-container *ngTemplateOutlet="rightPanelBottomTmpl"></ng-container>
                                </div>
                            </div>
                        </form>
                    </div>
                </ng-template>
            </p-splitter>
        </div>
    `
})
export class FormSplitterComponent implements AfterContentInit, OnInit {
    headerTmpl: TemplateRef<any>;
    leftPanelTmpl: TemplateRef<any>;
    leftPanelHeaderTmpl: TemplateRef<any>;
    rightPanelTopTmpl: TemplateRef<any>;
    rightPanelBottomTmpl: TemplateRef<any>;
    rightPanelHeaderActionsTmpl: TemplateRef<any>;
    eRightPanelTmpl: TemplateRef<any>;
    stateKey = `form-splitter-${this.userService.currentUser.id}/${this.location.path()}`;
    layout: "horizontal" | "vertical" = "vertical";

    @Input() header: string;
    @Input() headerIcon: string;
    @Input() subHeaderWarningMessage: string;
    @Input() subHeaderButtonMessage: string;
    @Input() rightSubHeader: string;
    @Input() leftSubHeader: string;
    @Input() eRightSubHeader: string;

    @Input() ravenEnabled: boolean = false;
    @Input() ravenValidConfig: boolean = false;

    /** If viewport's width is less than the break point then layout is vertical, otherwise horizontal  */
    @Input() layoutBreakPoint = 768;

    @Output() readonly ngLeftSubmit = new EventEmitter();
    @Output() readonly ngRightSubmit = new EventEmitter();
    @Output() readonly subHeaderButton = new EventEmitter();
    @Output() readonly ngERightSubmit = new EventEmitter();

    @ContentChildren(PrimeTemplate) _contentTemplates: QueryList<PrimeTemplate>;

    @ViewChild(FormStateComponent) formState: FormStateComponent;

    constructor(private userService: UserService, private location: Location) {}

    ngAfterContentInit(): void {
        this._contentTemplates.forEach(pTmpl => {
            switch (pTmpl.getType()) {
                case "header":
                    this.headerTmpl = pTmpl.template;
                    break;
                case "leftPanel":
                    this.leftPanelTmpl = pTmpl.template;
                    break;
                case "leftPanelHeader":
                    this.leftPanelHeaderTmpl = pTmpl.template;
                    break;
                case "rightPanelHeaderActions":
                    this.rightPanelHeaderActionsTmpl = pTmpl.template;
                    break;
                case "rightPanelTop":
                    this.rightPanelTopTmpl = pTmpl.template;
                    break;
                case "rightPanelBottom":
                    this.rightPanelBottomTmpl = pTmpl.template;
                    break;
                case "eRightPanel":
                    this.eRightPanelTmpl = pTmpl.template;
                    break;
            }
        });
    }

    @HostListener("window:resize")
    ngOnInit(): void {
        this.layout = window.innerWidth < this.layoutBreakPoint ? "vertical" : "horizontal";
    }
}
